import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState } from '../stores/types/types';
import { FkTable } from '../stores/types/content';

const PPTX_DECK_CONTENT_TYPE = 'pptx-deck';

interface PptxDeckAnimations {
	hasAnimations: boolean;
	contentUrl: string | undefined;
}

/**
 * Resolves whether a media item is a converted PPTX deck with animations to play.
 *
 * The deck-summary `gr_generated_content` row has `contentType === 'pptx-deck'`,
 * keyed to the media item via `fkTable === 'gr_media'` && `fkId === mediaId`; its
 * `data` carries `totalAnimations`. It's stored as a JSON string in the DB, but
 * the store delivers it already parsed to an object — handle both. When there's
 * at least one animation, the media renders as an iframe pointed at `contentUrl`
 * — whose shim player layer handles next/prev/jump regardless of conversion
 * provider — instead of a static image from `media_data.image_array`. A deck
 * with zero animations gains nothing from the iframe, so it stays on images.
 */
const usePptxDeckAnimations = (mediaId: number): PptxDeckAnimations => {
	const generatedContent = useSelector(
		(s: GreenroomState) => s.GeneratedContentReducer.generatedContent,
	);

	return useMemo(() => {
		const row = generatedContent?.find(
			(x) => x.fkTable === FkTable.media
				// eslint-disable-next-line eqeqeq
				&& x.fkId == mediaId // loose: fkId can arrive as a string (matches use-media-memos)
				&& x.contentType === PPTX_DECK_CONTENT_TYPE,
		);
		let totalAnimations = 0;
		const rawData = row?.data as unknown;
		if (typeof rawData === 'string') {
			try {
				totalAnimations = JSON.parse(rawData)?.totalAnimations ?? 0;
			} catch {
				totalAnimations = 0;
			}
		} else if (rawData && typeof rawData === 'object') {
			totalAnimations = (rawData as { totalAnimations?: number }).totalAnimations ?? 0;
		}
		return {
			hasAnimations: !!row?.contentUrl && totalAnimations > 0,
			contentUrl: row?.contentUrl,
		};
	}, [generatedContent, mediaId]);
};

export default usePptxDeckAnimations;
